/**
 * Camada de IA (03/09/2026) — tipos e helpers PUROS da UI (a rede fica nos
 * componentes): espelho do contrato de api/ia-routes.ts e do formato de
 * resultado definido no system prompt (db/ia-prompts.ts).
 *
 * A IA PODE fugir do formato — `normalizarResultadoAnalise` aplica defaults
 * tolerantes para nunca quebrar a tela. A IA PROPÕE, NUNCA aplica: a única
 * ponte para escrita é `montarArquivoCorrecoesIa`, que gera o envelope
 * `divergencias-correcoes@v1` e o entrega ao fluxo de import JÁ EXISTENTE do
 * passo 7 (dry-run com prévia — a decisão final é sempre humana).
 */
import { SCHEMA_VERSAO_CORRECOES, type ArquivoCorrecoes } from "./correcoes-import";

// ---------------------------------------------------------------------------
// Configuração (GET/PUT /configuracoes/ia)
// ---------------------------------------------------------------------------

export interface ConfigIa {
  chaveConfigurada: boolean;
  chaveMascarada: string | null;
  modelo: string;
  /** Lista curada na Administração (02/09/2026): [] = SEM restrição — todos os
   *  modelos do catálogo ficam disponíveis para o usuário no passo 7. */
  modelosHabilitados: string[];
}

export interface ConfigIaSalva extends ConfigIa {
  chaveAtualizada: boolean;
}

// ---------------------------------------------------------------------------
// Catálogo de modelos (GET /ia/modelos) — preços em US$ por 1M tokens
// ---------------------------------------------------------------------------

export interface ModeloIa {
  id: string;
  nome: string;
  precoPromptUsd1M: number;
  precoRespostaUsd1M: number;
  contexto: number | null;
  /** 05/09/2026 — modelo de RACIOCÍNIO ("Thinking": pensa antes de responder;
   *  a análise com ele pode levar vários minutos). Vem do catálogo da OpenRouter. */
  raciocinio?: boolean;
}

/**
 * Estimativa de custo de UMA análise de competência. O dossiê das divergências
 * costuma ficar na casa das dezenas de milhares de tokens; assumimos ~60 mil
 * de prompt + ~4 mil de resposta (documentado no pedido do dono 03/09/2026).
 */
export const TOKENS_PROMPT_ANALISE = 60_000;
export const TOKENS_RESPOSTA_ANALISE = 4_000;

/** Custo estimado de uma análise no modelo (US$); null se o modelo não tem preço. */
export function custoAnaliseEstimado(modelo: ModeloIa | null | undefined): number | null {
  if (!modelo) return null;
  return (TOKENS_PROMPT_ANALISE * modelo.precoPromptUsd1M + TOKENS_RESPOSTA_ANALISE * modelo.precoRespostaUsd1M) / 1_000_000;
}

/** "US$ 0,0312" (4 casas — os valores por análise são centavos de dólar). */
export function fmtUsd(valor: number): string {
  return `US$ ${valor.toFixed(4).replace(".", ",")}`;
}

// ---------------------------------------------------------------------------
// Análise estruturada (POST /competencias/:id/ia/analisar)
// ---------------------------------------------------------------------------

export type LadoProvavel = "nosso" | "contmega" | "indefinido";

export type TipoAcaoIa = "correcao_v1" | "avisar_contmega" | "escalar_agente" | "nenhuma";

export interface AcaoIa {
  tipo: TipoAcaoIa;
  /** Ação do schema divergencias-correcoes@v1 (justificar/lancar_variavel/…). */
  correcao: Record<string, unknown> | null;
  textoContmega: string | null;
  resumoEscalacao: string | null;
}

export interface DivergenciaAnaliseIa {
  id: string;
  titulo: string;
  explicacao: string;
  /** Recomendação prática do que fazer (obrigatória no prompt desde 02/09/2026;
   *  tolerante: análises antigas podem não ter — null). */
  sugestao: string | null;
  ladoProvavel: LadoProvavel;
  evidencias: string[];
  acao: AcaoIa;
  /** Ciência SUGERIDA pela IA (02/09/2026): quando `pode`, a IA atesta que a
   *  divergência pode receber ciência com a justificativa fundamentada — a
   *  aplicação continua sendo um clique humano (justificar-lote). */
  autoJustificativa: { pode: boolean; justificativa: string | null } | null;
}

export interface ResultadoAnaliseIa {
  resumoGeral: string;
  divergencias: DivergenciaAnaliseIa[];
}

/** Registro persistido (POST analisar / GET analises) — resultado vem parseado. */
export interface AnaliseIa {
  id: string;
  competenciaId: number;
  modelo: string;
  criadoEm: string;
  tokensPrompt: number | null;
  tokensResposta: number | null;
  resultado: unknown;
  /** 02/09/2026 — escopo da análise: "abertas" (default) × "todas". */
  escopo?: string | null;
}

export const LADO_PROVAVEL_ROTULO: Record<LadoProvavel, string> = {
  nosso: "Provável erro nosso",
  contmega: "Provável erro da Contmega",
  indefinido: "Indefinido",
};

export const LADO_PROVAVEL_COR: Record<LadoProvavel, string> = {
  nosso: "blue",
  contmega: "orange",
  indefinido: "gray",
};

const TIPOS_ACAO: TipoAcaoIa[] = ["correcao_v1", "avisar_contmega", "escalar_agente", "nenhuma"];

function texto(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v : null;
}

/** Normalização TOLERANTE do JSON da IA: campos ausentes/fora do tipo ganham
 *  defaults visuais — a tela nunca quebra por causa do modelo. */
export function normalizarResultadoAnalise(bruto: unknown): ResultadoAnaliseIa {
  const obj = (typeof bruto === "object" && bruto !== null ? bruto : {}) as Record<string, unknown>;
  const resumoGeral = texto(obj.resumoGeral) ?? "A IA não devolveu um resumo geral.";
  const lista = Array.isArray(obj.divergencias) ? obj.divergencias : [];
  const divergencias: DivergenciaAnaliseIa[] = lista.map((item, i) => {
    const d = (typeof item === "object" && item !== null ? item : {}) as Record<string, unknown>;
    const a = (typeof d.acao === "object" && d.acao !== null ? d.acao : {}) as Record<string, unknown>;
    const tipoBruto = typeof a.tipo === "string" ? a.tipo : "nenhuma";
    const tipo: TipoAcaoIa = (TIPOS_ACAO as string[]).includes(tipoBruto) ? (tipoBruto as TipoAcaoIa) : "nenhuma";
    const ladoBruto = typeof d.ladoProvavel === "string" ? d.ladoProvavel : "indefinido";
    const ladoProvavel: LadoProvavel =
      ladoBruto === "nosso" || ladoBruto === "contmega" ? ladoBruto : "indefinido";
    const evidencias = Array.isArray(d.evidencias) ? d.evidencias.map(String).filter((e) => e.trim()) : [];
    // autoJustificativa (02/09/2026) — tolerante: ausente/fora do tipo = null
    const aj = (typeof d.autoJustificativa === "object" && d.autoJustificativa !== null
      ? d.autoJustificativa
      : null) as Record<string, unknown> | null;
    const autoJustificativa =
      aj && aj.pode === true
        ? { pode: true, justificativa: texto(aj.justificativa) }
        : null;
    return {
      id: texto(d.id) ?? `item-${i + 1}`,
      titulo: texto(d.titulo) ?? "Divergência analisada",
      explicacao: texto(d.explicacao) ?? "",
      sugestao: texto(d.sugestao),
      ladoProvavel,
      evidencias,
      autoJustificativa,
      acao: {
        tipo,
        correcao:
          tipo === "correcao_v1" && typeof a.correcao === "object" && a.correcao !== null
            ? (a.correcao as Record<string, unknown>)
            : null,
        textoContmega: tipo === "avisar_contmega" ? texto(a.textoContmega) : null,
        resumoEscalacao: tipo === "escalar_agente" ? texto(a.resumoEscalacao) : null,
      },
    };
  });
  return { resumoGeral, divergencias };
}

/** Monta o envelope divergencias-correcoes@v1 com a correção proposta pela IA
 *  (UMA ação por sugestão). Retorna null quando o item não traz correção
 *  aplicável — o botão "Preparar correção" nem aparece nesse caso. */
export function montarArquivoCorrecoesIa(item: DivergenciaAnaliseIa): ArquivoCorrecoes | null {
  if (item.acao.tipo !== "correcao_v1" || !item.acao.correcao) return null;
  const tipoCorrecao = typeof item.acao.correcao.tipo === "string" ? item.acao.correcao.tipo : "";
  if (!tipoCorrecao) return null;
  return {
    schemaVersao: SCHEMA_VERSAO_CORRECOES,
    descricao: `Sugestão da IA — ${item.titulo} (divergência ${item.id}). A IA propõe; a decisão de aplicar é sua.`,
    aplicar: false,
    acoes: [item.acao.correcao],
  };
}

// ---------------------------------------------------------------------------
// Chat contextual (GET/POST /competencias/:id/ia/chat)
// ---------------------------------------------------------------------------

/** Anexo da mensagem do chat (04/09/2026 — chat agêntico): upload antecipado
 *  via POST .../ia/anexos; o conteúdo é servido por GET /ia/anexos/:id. */
export interface AnexoChatIa {
  id: string;
  nome: string;
  mime: string;
  tamanhoBytes: number;
}

/** Uma chamada de ferramenta registrada na trilha da resposta do assistente. */
export interface ChamadaFerramentaMeta {
  nome: string;
  /** Argumentos da chamada (objeto JSON livre). */
  argumentos?: unknown;
  resumo?: string | null;
}

/** Justificativa de divergência PROPOSTA pela IA (ferramenta
 *  propor_justificativa) — nunca gravada pela IA; a UI mostra o botão
 *  "Aplicar justificativa" e o clique humano chama POST /divergencias/:id/justificar. */
export interface PropostaJustificativa {
  divergenciaId: string;
  justificativa: string;
  referenciaEstudo?: string | null;
}

export interface SubagenteMeta {
  objetivo: string;
  resumo: string;
}

/** Trilha da resposta do assistente (ia_mensagens.metaJson, parseado pela API).
 *  Ausente em mensagens antigas — parse TOLERANTE obrigatório. */
export interface MetaMensagemIa {
  modo?: string;
  rodadas?: number;
  ferramentas?: ChamadaFerramentaMeta[];
  propostas?: PropostaJustificativa[];
  subagentes?: SubagenteMeta[];
  limiteAtingido?: boolean;
}

export interface MensagemChatIa {
  id: string;
  competenciaId: number;
  papel: "user" | "assistant";
  conteudo: string;
  modelo: string | null;
  criadoEm: string;
  meta?: MetaMensagemIa | null;
  anexos?: AnexoChatIa[];
}

/** Normalização TOLERANTE do meta da mensagem: aceita o objeto parseado da API
 *  (ou uma string JSON, por defesa) e filtra itens fora do shape — mensagens
 *  antigas sem meta devolvem null e a tela nunca quebra. */
export function normalizarMetaMensagem(bruto: unknown): MetaMensagemIa | null {
  if (typeof bruto === "string" && bruto.trim()) {
    try {
      bruto = JSON.parse(bruto);
    } catch {
      return null;
    }
  }
  if (!bruto || typeof bruto !== "object") return null;
  const o = bruto as Record<string, unknown>;
  const ferramentas: ChamadaFerramentaMeta[] = (Array.isArray(o.ferramentas) ? o.ferramentas : [])
    .filter((f): f is Record<string, unknown> => typeof f === "object" && f !== null)
    .filter((f) => typeof f.nome === "string" && f.nome !== "")
    .map((f) => ({
      nome: f.nome as string,
      argumentos: f.argumentos,
      resumo: typeof f.resumo === "string" ? f.resumo : null,
    }));
  const propostas: PropostaJustificativa[] = (Array.isArray(o.propostas) ? o.propostas : [])
    .filter((p): p is Record<string, unknown> => typeof p === "object" && p !== null)
    .filter((p) => typeof p.divergenciaId === "string" && typeof p.justificativa === "string")
    .map((p) => ({
      divergenciaId: p.divergenciaId as string,
      justificativa: p.justificativa as string,
      referenciaEstudo: typeof p.referenciaEstudo === "string" ? p.referenciaEstudo : null,
    }));
  const subagentes: SubagenteMeta[] = (Array.isArray(o.subagentes) ? o.subagentes : [])
    .filter((s): s is Record<string, unknown> => typeof s === "object" && s !== null)
    .map((s) => ({
      objetivo: typeof s.objetivo === "string" ? s.objetivo : "",
      resumo: typeof s.resumo === "string" ? s.resumo : "",
    }));
  return {
    modo: typeof o.modo === "string" ? o.modo : undefined,
    rodadas: typeof o.rodadas === "number" ? o.rodadas : undefined,
    ferramentas,
    propostas,
    subagentes,
    limiteAtingido: o.limiteAtingido === true,
  };
}

// ---------------------------------------------------------------------------
// Bloco de AÇÃO no chat (02/09/2026) — o assistente pode terminar a resposta
// com EXATAMENTE UM bloco fenced ```acao contendo UMA ação JSON do schema
// divergencias-correcoes@v1. O front ESCONDE o bloco do texto e o transforma
// em botão "Preparar correção" (mesmo fluxo de import com dry-run — a IA
// propõe, quem aplica é o dono). Parse TOLERANTE: bloco malformado vira texto
// normal com aviso discreto, nunca quebra a conversa.
// ---------------------------------------------------------------------------

export interface AcaoDoChat {
  /** Texto da resposta SEM o bloco ```acao (pronto para renderizar). */
  textoSemBloco: string;
  /** Ação parseada (uma ação do schema divergencias-correcoes@v1). */
  acao: Record<string, unknown> | null;
  /** true quando havia um fence ```acao mas o JSON não parseou — renderizar
   *  o conteúdo como texto normal + aviso discreto. */
  malformado: boolean;
}

/** Extrai o (único) bloco fenced ```acao da resposta do assistente. */
export function extrairAcaoDoChat(conteudo: string): AcaoDoChat {
  // tolera fence sem quebra de linha, com \r\n e fence final ausente (stream cortado)
  const m = conteudo.match(/```acao[^\S\r\n]*\r?\n?([\s\S]*?)(?:```|$)/);
  if (!m) return { textoSemBloco: conteudo, acao: null, malformado: false };
  const bruto = (m[1] ?? "").trim();
  const textoSemBloco = (conteudo.slice(0, m.index) + conteudo.slice(m.index! + m[0].length)).trim();
  try {
    const parsed: unknown = JSON.parse(bruto);
    if (typeof parsed === "object" && parsed !== null && typeof (parsed as { tipo?: unknown }).tipo === "string") {
      return { textoSemBloco, acao: parsed as Record<string, unknown>, malformado: false };
    }
    // JSON válido mas não é uma ação (sem "tipo") — mostra como texto normal
    return { textoSemBloco: conteudo, acao: null, malformado: true };
  } catch {
    return { textoSemBloco: conteudo, acao: null, malformado: true };
  }
}

/** Monta o envelope divergencias-correcoes@v1 com UMA ação vinda do chat.
 *  Retorna null quando a ação não tem tipo — o botão nem aparece. */
export function montarArquivoCorrecoesDeAcao(acao: Record<string, unknown>): ArquivoCorrecoes | null {
  const tipo = typeof acao.tipo === "string" ? acao.tipo : "";
  if (!tipo) return null;
  return {
    schemaVersao: SCHEMA_VERSAO_CORRECOES,
    descricao: `Ação sugerida pela IA no chat (${tipo}). A IA propõe; a decisão de aplicar é sua — revise a prévia antes.`,
    aplicar: false,
    acoes: [acao],
  };
}
